/**
 * SF-5127 — "Reply from" (`help_desk_reply_from`), shown while the company forwards its own
 * address: help desk emails come from a ServeFirst address under the company's name (the
 * default), or from the company's support inbox address itself.
 *
 * The support address can only be picked once the company has added the sending records for the
 * domain of its support inbox address and every one checks out (the sending domain's `valid`);
 * sf-api refuses it otherwise. Below the choice: set up the records, the records table with
 * copy buttons and whether each one was found, Check again, and Remove (Reply from then goes
 * back to ServeFirst). A DMARC record, when the domain has none, is shown apart as recommended
 * only: it is never checked and never blocks the support address. Every write goes through the
 * page's saver (`send`), one at a time per company, and the company and the domain are re-read
 * after it, a failed one too: a failed check can still have found the domain no longer verified.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Radio, Space, Table, Tag, Typography } from 'antd';
import { HelpDeskRow } from './HelpDeskRow';
import type { HelpDeskTabProps } from './HelpDeskTab';
import {
  useHelpDeskSendingDomain,
  useSendingDomainAction,
  type HelpDeskDnsRecord,
  type SendingDomainAction,
} from '../api/helpDeskSendingDomainClient';
import { emailDomain } from '../api/csatDomainClient';
import type { Company, HelpDeskReplyFrom as ReplyFrom } from '../list';

const { Text } = Typography;

const REPLY_FROM_OPTIONS: Array<{ value: ReplyFrom; label: string; note: string }> = [
  {
    value: 'servefirst',
    label: 'ServeFirst',
    note: "Emails come from a ServeFirst address under the company's name.",
  },
  {
    value: 'support_address',
    label: "The company's support address",
    note: "Emails come from the company's support inbox address under the company's name.",
  },
];

const NO_INBOX_NOTE =
  "To send from the company's support address, add the support inbox address first.";
const notVerifiedNote = (domain: string) =>
  `The company's support address can be picked once every record for ${domain} is found.`;

const ACTION_SAVED: Record<SendingDomainAction, string> = {
  setup: 'Sending records made',
  check: 'Sending records checked',
  remove: 'Sending records removed',
};

const FoundTag: React.FC<{ valid: boolean | null }> = ({ valid }) =>
  valid === true ? (
    <Tag color="success">Yes</Tag>
  ) : valid === false ? (
    <Tag color="error">No</Tag>
  ) : (
    <Tag>Not checked yet</Tag>
  );

const RECORD_COLUMNS = [
  { title: 'Type', dataIndex: 'kind', key: 'kind', width: 80 },
  {
    title: 'Host',
    dataIndex: 'host',
    key: 'host',
    render: (host: string) => (
      <Text code copyable>
        {host}
      </Text>
    ),
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
    render: (value: string) => (
      <Text code copyable>
        {value}
      </Text>
    ),
  },
  {
    title: 'Found',
    dataIndex: 'valid',
    key: 'valid',
    width: 130,
    render: (valid: boolean | null) => <FoundTag valid={valid} />,
  },
];

const DmarcSuggestion: React.FC<{ host: string; value: string }> = ({ host, value }) => (
  <Space direction="vertical" size={2} data-testid="help-desk-dmarc-suggestion">
    <Space wrap>
      <Text strong>DMARC record (TXT)</Text>
      <Tag>Recommended, not required</Tag>
    </Space>
    <Text type="secondary">
      No DMARC record was found. Adding this one helps emails reach inboxes. It is not checked, and
      the support address can be picked without it.
    </Text>
    <Space wrap>
      <Text type="secondary">Host</Text>
      <Text code copyable>
        {host}
      </Text>
      <Text type="secondary">Value</Text>
      <Text code copyable>
        {value}
      </Text>
    </Space>
  </Space>
);

export const HelpDeskReplyFrom: React.FC<{
  company: Company;
  locked: boolean;
  reason: string | null;
  saver: HelpDeskTabProps['saver'];
}> = ({ company, locked, reason, saver }) => {
  const companyId = String(company._id ?? '');
  const savedReplyFrom: ReplyFrom =
    company.help_desk_reply_from === 'support_address' ? 'support_address' : 'servefirst';
  const pendingReplyFrom =
    saver.pending?.field === 'help_desk_reply_from'
      ? (saver.pending.value as ReplyFrom)
      : undefined;
  const inboxDomain = emailDomain(company.assets_support_email ?? '');

  const query = useHelpDeskSendingDomain(companyId, true);
  const domain = query.data ?? null;
  const domainMatchesInbox = !!domain && domain.domain === inboxDomain;
  // A failed read keeps the last domain it had, which may no longer be true: never pickable then.
  const canPickSupport = !query.isError && domain?.valid === true && domainMatchesInbox;

  const runAction = useSendingDomainAction();
  const [running, setRunning] = useState<SendingDomainAction | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => {
    if (locked) setConfirmRemove(false);
  }, [locked]);
  // Reply from is the company field these actions can change (Remove puts it back to ServeFirst).
  const act = async (action: SendingDomainAction) => {
    setRunning(action);
    try {
      await saver.save(
        'help_desk_reply_from',
        action === 'remove' ? 'servefirst' : savedReplyFrom,
        ACTION_SAVED[action],
        { send: runAction(action), rereadRequired: true },
      );
    } finally {
      setRunning(null);
    }
  };

  const pickReplyFrom = (next: ReplyFrom) => {
    if (locked || next === savedReplyFrom) return;
    if (next === 'support_address' && !canPickSupport) return;
    void saver.save(
      'help_desk_reply_from',
      next,
      next === 'support_address'
        ? "Emails now come from the company's support address"
        : 'Emails now come from ServeFirst',
    );
  };

  const shownReplyFrom = pendingReplyFrom ?? savedReplyFrom;
  const whyNotSupport = !inboxDomain
    ? NO_INBOX_NOTE
    : query.isSuccess && !canPickSupport
      ? notVerifiedNote(inboxDomain)
      : null;

  return (
    <HelpDeskRow
      testId="help-desk-reply-from"
      title="Reply from"
      note={
        reason ?? (
          <>
            {REPLY_FROM_OPTIONS.find((o) => o.value === shownReplyFrom)?.note}
            {whyNotSupport ? (
              <>
                {' '}
                <span data-testid="help-desk-reply-from-blocked">{whyNotSupport}</span>
              </>
            ) : null}
          </>
        )
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Radio.Group
          aria-label="Reply from"
          name="help-desk-reply-from"
          value={shownReplyFrom}
          disabled={locked}
          onChange={(e) => pickReplyFrom(e.target.value as ReplyFrom)}
        >
          {REPLY_FROM_OPTIONS.map((o) => (
            <Radio
              key={o.value}
              value={o.value}
              disabled={o.value === 'support_address' && !canPickSupport}
            >
              {o.label}
            </Radio>
          ))}
        </Radio.Group>

        {query.isLoading && query.fetchStatus !== 'idle' ? (
          <Text type="secondary">Loading the sending records…</Text>
        ) : query.isError ? (
          <Alert
            type="error"
            showIcon
            message={
              domain
                ? `Could not load the current state of the sending records for ${domain.domain}.`
                : 'Could not load the sending records.'
            }
            action={
              <Button size="small" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          />
        ) : !domain ? (
          inboxDomain ? (
            <Space direction="vertical" data-testid="help-desk-sending-domain">
              <Button
                disabled={locked}
                loading={running === 'setup'}
                onClick={() => void act('setup')}
              >
                {`Set up sending from ${inboxDomain}`}
              </Button>
              <Text type="secondary">
                Makes the DNS records the company adds for {inboxDomain}. They only allow sending;
                the company&apos;s own email and website are not touched.
              </Text>
            </Space>
          ) : null
        ) : (
          <Space
            direction="vertical"
            style={{ width: '100%' }}
            data-testid="help-desk-sending-domain"
          >
            <Space wrap>
              <Text strong>Sending records for {domain.domain}</Text>
              {domain.valid ? (
                <Tag color="success">Verified</Tag>
              ) : (
                <Tag>{domain.validated_at ? 'Not verified' : 'Not verified yet'}</Tag>
              )}
            </Space>
            {!domainMatchesInbox ? (
              <Alert
                type="warning"
                showIcon
                message={
                  inboxDomain
                    ? `These records are for ${domain.domain}, but the support inbox address is on ${inboxDomain}. Remove them and set up ${inboxDomain}.`
                    : `These records are for ${domain.domain}, but the company has no support inbox address.`
                }
              />
            ) : null}
            {domain.error_reason ? (
              <Alert type="warning" showIcon message={domain.error_reason} />
            ) : null}
            <Table<HelpDeskDnsRecord>
              size="small"
              pagination={false}
              rowKey={(r) => `${r.kind}-${r.host}`}
              dataSource={domain.records}
              columns={RECORD_COLUMNS}
              locale={{ emptyText: 'No records yet' }}
            />
            {domain.dmarc_suggestion ? (
              <DmarcSuggestion
                host={domain.dmarc_suggestion.host}
                value={domain.dmarc_suggestion.value}
              />
            ) : null}
            <Space wrap>
              <Button
                disabled={locked}
                loading={running === 'check'}
                onClick={() => void act('check')}
              >
                Check again
              </Button>
              <Popconfirm
                title={`Remove sending from ${domain.domain}?`}
                description="Emails go back to coming from ServeFirst. The company can then delete these DNS records."
                okText="Remove"
                okButtonProps={{ danger: true }}
                open={confirmRemove && !locked}
                onOpenChange={(open) => setConfirmRemove(open && !locked)}
                onConfirm={() => {
                  setConfirmRemove(false);
                  if (!locked) void act('remove');
                }}
              >
                <Button danger disabled={locked} loading={running === 'remove'}>
                  Remove
                </Button>
              </Popconfirm>
              <Text type="secondary">
                {domain.validated_at
                  ? `Last verified ${new Date(domain.validated_at).toLocaleString()}`
                  : 'Not verified yet'}
              </Text>
            </Space>
          </Space>
        )}
      </Space>
    </HelpDeskRow>
  );
};
